import { EventType } from "@/analytics/analytics-events";
import { SwitchPointCloudStreamEventProperties } from "@/analytics/generic-events-properties";
import { selectActiveElementReference } from "@/modes/mode-selectors";
import {
  CurrentAreaData,
  selectIsPointCloudViewable,
} from "@/modes/select-current-area-data";
import {
  FeaturesState,
  selectFeaturesSlice,
} from "@/store/features/features-slice";
import {
  OnlyIElementsStore,
  selectIElementsSlice,
} from "@/store/ielements-slice-selector";
import { curryAppSelector } from "@/store/reselect";
import { RootState } from "@/store/store";
import { useAppSelector } from "@/store/store-hooks";
import {
  FaroMenu,
  FaroMenuProps,
  PointCloudFlashSmallIcon,
  PointCloudIcon,
  PointCloudTrajectorySmallIcon,
  SingleSelectItem,
} from "@faro-lotv/flat-ui";
import { Analytics } from "@faro-lotv/foreign-observers";
import {
  GUID,
  IElement,
  IElementGenericPointCloudStream,
  IElementSection,
  isIElementGenericPointCloudStream,
} from "@faro-lotv/ielement-types";
import {
  IElementsState,
  PointCloudType,
  selectChildrenDepthFirst,
  selectPointCloudType,
} from "@faro-lotv/project-source";
import { SvgIconProps } from "@mui/material";
import { createSelector } from "@reduxjs/toolkit";
import { useCallback, type JSX } from "react";

type PointCloudToggleMenuProps = FaroMenuProps & {
  /** Selector for getting the currently active element in this View */
  currentMainElement?: IElement;
  /** The point cloud streams to toggle between */
  pointCloudStreams: IElementGenericPointCloudStream[];
  /** Callback for when the active element is changed */
  onActiveElementChanged(id: GUID): void;
};

/** @returns Toggle between the different point clouds within a dataset. */
export function PointCloudToggleMenu({
  currentMainElement,
  onActiveElementChanged,
  pointCloudStreams,
  sx,
  dark,
  ...rest
}: PointCloudToggleMenuProps): JSX.Element | null {
  return (
    <FaroMenu
      dark={dark}
      sx={[
        {
          mt: 0.5,
          "& .MuiList-root": {
            display: "flex",
            flexFlow: "column",
            gap: 0.5,
          },
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      {...rest}
    >
      {pointCloudStreams.map((pointCloudStream) => (
        <PointCloudToggleButton
          dark={dark}
          key={pointCloudStream.id}
          pointCloudStream={pointCloudStream}
          activeElement={currentMainElement}
          onActiveElementChanged={onActiveElementChanged}
        />
      ))}
    </FaroMenu>
  );
}

type PointCloudToggleButtonProps = {
  /** The point cloud to toggle to when clicking the button. */
  pointCloudStream: IElementGenericPointCloudStream;

  /** The currently selected element. */
  activeElement?: IElement;

  /** Callback for when the active element is changed */
  onActiveElementChanged(id: GUID): void;

  /** True to use the dark styling for the Button */
  dark?: boolean;
};

function PointCloudToggleButton({
  pointCloudStream,
  activeElement,
  onActiveElementChanged,
  dark,
}: PointCloudToggleButtonProps): JSX.Element {
  const displayName = usePointCloudStreamTypeName(pointCloudStream);

  const Icon = useCallback(
    (props: SvgIconProps) => (
      <PointCloudStreamIcon pointCloudStream={pointCloudStream} {...props} />
    ),
    [pointCloudStream],
  );

  const pointCloudType = useAppSelector(selectPointCloudType(pointCloudStream));

  return (
    <SingleSelectItem
      Icon={Icon}
      label={displayName}
      key={pointCloudStream.id}
      value={pointCloudStream.id}
      selectedValue={activeElement?.id}
      onClick={() => {
        Analytics.track<SwitchPointCloudStreamEventProperties>(
          EventType.switchPointCloudStream,
          {
            pointCloudType,
          },
        );
        onActiveElementChanged(pointCloudStream.id);
      }}
      dark={dark}
    />
  );
}

/**
 * @param state The state of the app store
 * @param activeDataSession The currently active element to consider the dataset of.
 * @returns All point cloud streams in the data session of the given element.
 */
const selectPointCloudStreamsInSameSession = curryAppSelector(
  createSelector(
    [
      selectIElementsSlice,
      (
        state: OnlyIElementsStore,
        activeDataSession: IElementSection | undefined,
      ) => activeDataSession,
    ],
    computePointCloudStreamsInSameSession,
  ),
);

function computePointCloudStreamsInSameSession(
  iElements: IElementsState,
  activeDataSession: IElementSection | undefined,
): IElementGenericPointCloudStream[] {
  const iElementsStore = { iElements };

  const pointCloudStreams = selectChildrenDepthFirst(
    activeDataSession,
    isIElementGenericPointCloudStream,
  )(iElementsStore);

  // Return streams sorted by type instead of order in the project
  const streamAndType = [];
  for (const stream of pointCloudStreams) {
    const type = selectPointCloudType(stream)(iElementsStore);
    streamAndType.push({ stream, type });
  }

  streamAndType.sort(
    (a, b) =>
      PointCloudTypesSortingOrder.indexOf(a.type) -
      PointCloudTypesSortingOrder.indexOf(b.type),
  );

  return streamAndType
    .map(({ stream }) => stream)
    .filter((pc) => selectIsPointCloudViewable(pc)(iElementsStore));
}

/**
 * A memoized selector to compute all viewable point-clouds that should be selectable in the point cloud toggle.
 *
 * @param state The state of the app store
 * @param features The features slice of the store,
 * @param element The currently active element to consider the dataset of.
 * @param area The data of the current area
 * @returns the memoized selector
 */
export const selectViewablePointCloudStreamsInSameSession = curryAppSelector(
  createSelector(
    [
      selectIElementsSlice,
      selectFeaturesSlice,
      (state: RootState, element: IElement | undefined) => element,
      (
        state: RootState,
        element: IElement | undefined,
        area: CurrentAreaData,
      ) => area,
    ],
    computeViewablePointCloudStreamsInSameSession,
  ),
);

/**
 * This selector returns all viewable point-clouds that should be selectable in the point cloud toggle.
 * This excludes, e.g. single scan point clouds.
 *
 * @param iElements The iElements slice of the store
 * @param features The features slice of the store,
 * @param element The currently active element to consider the dataset of.
 * @param area The data of the current area
 * @returns All viewable point cloud streams in the data session of the given element.
 */
function computeViewablePointCloudStreamsInSameSession(
  iElements: IElementsState,
  features: FeaturesState,
  element: IElement | undefined,
  area: CurrentAreaData,
): IElementGenericPointCloudStream[] {
  // To speed up computation time, we split the computation in two steps.
  // first, the active data session is computed. Notably, if the user is in walk mode and
  // jumps from a pano to the next, this active data session remains the same.
  // Then, the real computation of the viewable point cloud streams is done in a
  // memoized selector, only if the active data session really changed.
  const store = { iElements, features };
  const activeDataSession = selectActiveElementReference(element, area)(store);

  if (!activeDataSession) return [];

  return selectPointCloudStreamsInSameSession(activeDataSession)(store);
}

/**
 * @param pointCloudStream The point cloud stream to determine the type name of.
 * @returns A user-suitable string to describe the type of point cloud scan.
 */
function usePointCloudStreamTypeName(
  pointCloudStream: IElementGenericPointCloudStream,
): string {
  const pointCloudType = useAppSelector(selectPointCloudType(pointCloudStream));

  switch (pointCloudType) {
    case PointCloudType.flash:
      return "Flash scan";
    case PointCloudType.mobile:
      return "Mobile scan";
    case PointCloudType.project:
    case PointCloudType.singleScan:
    case PointCloudType.other:
      return "Point cloud";
  }
}

type PointCloudIconProps = SvgIconProps & {
  /** The point cloud to generate the icon for. */
  pointCloudStream: IElementGenericPointCloudStream;
};

/**
 * @returns An icon representing the type of scan that the point cloud was produced with.
 */
export function PointCloudStreamIcon({
  pointCloudStream,
  ...rest
}: PointCloudIconProps): JSX.Element {
  const pointCloudType = useAppSelector(selectPointCloudType(pointCloudStream));

  switch (pointCloudType) {
    case PointCloudType.flash:
      return <PointCloudFlashSmallIcon {...rest} />;
    case PointCloudType.mobile:
      return <PointCloudTrajectorySmallIcon {...rest} />;
    case PointCloudType.project:
    case PointCloudType.singleScan:
    case PointCloudType.other:
      return <PointCloudIcon {...rest} />;
  }
}

/**
 * Order of Point Cloud types for sorting, to guarantee a deterministic order.
 * Roughly sorted from generic to specialized.
 */
const PointCloudTypesSortingOrder = [
  PointCloudType.other,
  PointCloudType.project,
  PointCloudType.singleScan,
  PointCloudType.mobile,
  PointCloudType.flash,
];
